import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { TickMarkWithBackgroundIcon } from '@/components/icons/TickMarkWithBackgroundIcon';
import { FormulaBenefitsSheet } from './FormulaBenefitsSheet';
import {
  FormulaProduct,
  useMergedShopFormulas,
  useGetFormulaBenefits,
} from '@viome/viome-base/src/usecase/Shop/ShopQuery';
import { useFindPlanBySkuSet } from '@viome/viome-base/src/usecase/Shop/ShopUtil';
import { ErrorLoadingPage } from '@screens/ErrorHandling/ErrorLoadingPage';
import { ProductDetailSkeleton } from '@screens/ShopPage/components/ProductDetailSkeleton';
import { FormulaIngredientsSheet } from '@screens/ShopPage/components/FormulaIngredientsSheet';
import { Button } from '@/components/design-system/Button';
import { EventTracker } from '@/utils';
import { analyticsEvent } from '@utils/analyticsEvent';
import { useManageSubscriptionLink } from '@/hooks/useManageSubscriptionLink';

export const ProductDetailPage: React.FC = () => {
  const {
    data: mergedShopFormulaData,
    isLoading: isLoadingFormulas,
    isError: isErrorFormulas,
  } = useMergedShopFormulas();
  const { id: productId } = useParams();
  const { data: formulaBenefitsData } = useGetFormulaBenefits(
    Number(productId),
  );
  const navigate = useNavigate();
  const manageSubscriptionLink = useManageSubscriptionLink();
  const [isScrolled, setIsScrolled] = useState(false);
  const productsData: FormulaProduct[] | undefined =
    mergedShopFormulaData?.products;

  const product: FormulaProduct | undefined = productsData?.find(
    (p) => p.productId === productId,
  );

  const shouldShowButton =
    (formulaBenefitsData?.goals?.length ?? 0) > 0 ||
    (formulaBenefitsData?.scores?.length ?? 0) > 0 ||
    (formulaBenefitsData?.connections?.length ?? 0) > 0;

  const { plan: productPlan } = useFindPlanBySkuSet(
    product?.sku ? [product.sku] : [],
  );

  const dailyPrice = useMemo(() => {
    if (productPlan?.plan_product?.price) {
      const price = parseFloat(productPlan.plan_product.price);
      const discountPercentage = mergedShopFormulaData?.discountPercentage;

      if (typeof discountPercentage === 'number' && discountPercentage > 0) {
        const discountedPrice = price * (1 - discountPercentage / 100);
        return discountedPrice / 30;
      }
      return price / 30;
    }
    return null;
  }, [productPlan, mergedShopFormulaData?.discountPercentage]);

  const anySubscribed = useMemo(() => {
    return (
      mergedShopFormulaData?.products?.some((p) => p.is_subscribed) ?? false
    );
  }, [mergedShopFormulaData?.products]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Determine if the title contains the trademark symbol for styling
  const titleParts = product?.title?.split('™');
  const mainTitle = titleParts && titleParts[0];
  const trademark = titleParts && titleParts.length > 1 ? '™' : '';

  if (isLoadingFormulas) {
    return <ProductDetailSkeleton />;
  }

  if (isErrorFormulas || !productsData || !product) {
    return <ErrorLoadingPage errorType="error" hideNavBar />;
  }

  const handleBuildMyPlanClick = () => {
    if (anySubscribed) {
      navigate(manageSubscriptionLink);
    } else {
      navigate('/shop');
    }

    if (product?.productId === '1') {
      EventTracker.trackEvent(
        'shop',
        analyticsEvent.SHOP_PRODUCTDETAILS_ORALLOZENGES_BUILDMYPLANCTA,
      );
    } else if (product?.productId === '2') {
      EventTracker.trackEvent(
        'shop',
        analyticsEvent.SHOP_PRODUCTDETAILS_SUPPLEMENTS_BUILDMYPLANCTA,
      );
    } else if (product?.productId === '3') {
      EventTracker.trackEvent(
        'shop',
        analyticsEvent.SHOP_PRODUCTDETAILS_GUTFORMULA_BUILDMYPLANCTA,
      );
    } else if (product?.productId === '4') {
      EventTracker.trackEvent(
        'shop',
        analyticsEvent.SHOP_PRODUCTDETAILS_TOOTHPASTEANDGEL_BUILDMYPLANCTA,
      );
    }
  };

  const onClickProductTab = (product: FormulaProduct) => {
    if (product?.productId === '1') {
      EventTracker.trackEvent(
        'shop',
        analyticsEvent.SHOP_PRODUCTDETAILS_TAB_ORALLOZENGES,
      );
    } else if (product?.productId === '2') {
      EventTracker.trackEvent(
        'shop',
        analyticsEvent.SHOP_PRODUCTDETAILS_TAB_SUPPLEMENTS,
      );
    } else if (product?.productId === '3') {
      EventTracker.trackEvent(
        'shop',
        analyticsEvent.SHOP_PRODUCTDETAILS_TAB_GUTFORMULA,
      );
    } else if (product?.productId === '4') {
      EventTracker.trackEvent(
        'shop',
        analyticsEvent.SHOP_PRODUCTDETAILS_TAB_TOOTHPASTEANDGEL,
      );
    }

    navigate(`/shop/product/${product.productId}`);
  };

  return (
    <section
      data-product={product?.productId}
      className="transition-colors min-h-screen bg-gradient-to-b data-[product=2]:from-[#F0F7F1] data-[product=2]:to-[#F3F3F0] data-[product=3]:from-[#F5F4ED] data-[product=3]:to-[#F2EEEB] data-[product=1]:from-[#E8ECFC] data-[product=1]:to-[#F0F6F7] from-[#E4F0F6] to-[#F4F2ED]"
    >
      <div
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled ? 'shadow-sm' : ''
        }`}
        style={{
          backgroundColor: isScrolled
            ? 'rgba(113, 115, 132, 0.08)'
            : 'transparent',
          backdropFilter: isScrolled ? 'blur(37px)' : 'none',
          WebkitBackdropFilter: isScrolled ? 'blur(37px)' : 'none', // For Safari
          borderBottom: isScrolled ? '1px solid rgba(0, 0, 0, 0.05)' : 'none',
        }}
      >
        <div className="page-container flex items-center justify-between">
          <button
            onClick={() => navigate('/shop?tab=formulas')}
            className="text-gray-600 hover:text-gray-800"
          >
            <ChevronLeft className="h-6 w-6" />
            <span className="sr-only">Back</span>
          </button>
          <div className="flex items-center space-x-1 bg-[#F4F4F4] p-1 rounded-full">
            {productsData?.slice(0, 4).map((p) => (
              <div
                key={p.productId}
                onClick={() => onClickProductTab(p)}
                className={`p-1 rounded-full cursor-pointer ${p.productId === product.productId ? 'bg-white shadow-md' : 'hover:bg-gray-200'}`}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    navigate(`/shop/product/${p.productId}`);
                  }
                }}
                aria-label={`View product ${p.title}`}
              >
                <img
                  src={p.productImageUrl}
                  alt={p.title}
                  className="h-8 w-8 rounded-full object-cover"
                />
              </div>
            ))}
          </div>
          <div></div>
        </div>
      </div>
      <div className="page-container">
        <div className="mx-auto p-4 md:p-6 lg:p-8">
          <div className="text-center mb-10">
            <img
              src={product.productImageUrl}
              alt={product.title}
              className="h-36 mx-auto mb-6 w-36"
            />
            {/* {product?.product_id != 2 && (
              <div className="flex items-center justify-center mb-2.5">
                <VRXIcon />
                My·Biotics™
              </div>
            )} */}
            <h1 className="typography-display2 text-primary mb-6">
              {mainTitle}
              {trademark && <sup className="text-primary">{trademark}</sup>}
            </h1>
            <p className="typography-headline2 font-serif italic mb-8">
              Made for you
            </p>
            {dailyPrice !== null && (
              <div className="text-success text-xl typography-headline2 mb-6">
                Starting at ${dailyPrice.toFixed(2)}/day
              </div>
            )}
            <div className="flex justify-center space-x-3 mb-8">
              <FormulaIngredientsSheet
                productId={Number(productId)}
                ingredientCount={product?.ingredients_count}
              />
              {shouldShowButton && (
                <FormulaBenefitsSheet
                  productId={Number(productId)}
                  formulaBenefitsData={formulaBenefitsData}
                  productName={product.title}
                  productImageSrc={product.productImageUrl}
                />
              )}
            </div>

            <p className="typography-body max-w-xl text-primary mx-auto mb-6">
              {product.longDescription}
            </p>

            <Button
              variant="default"
              className="mb-14 shadow-md hover:opacity-90 transition-opacity px-5 py-3 rounded-lg typography-body"
              disabled={product.is_subscribed}
              onClick={
                product.is_subscribed ? undefined : handleBuildMyPlanClick
              }
            >
              {product.is_subscribed ? 'Subscribed' : 'Build My Plan'}
            </Button>
          </div>

          {product.promoVideoUrl && (
            <div className="mb-16 rounded-lg overflow-hidden shadow-xl max-w-xl mx-auto">
              <video
                src={product.promoVideoUrl}
                className="w-full h-auto"
                autoPlay
                loop
                muted
              >
                Your browser does not support the video tag.
              </video>
            </div>
          )}

          <div className="text-center mb-12">
            <h2 className="typography-h2 text-primary mb-16">
              {product.formulaShortDescription}
            </h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {product.formulaBenefits?.map((benefit, index) => (
              <div
                key={index}
                className="flex flex-col items-center text-center p-4"
              >
                <TickMarkWithBackgroundIcon className="mb-4" />
                <p className="typography-btn-medium text-primary max-w-[192px]">
                  {benefit}
                </p>
              </div>
            ))}
          </div>

          <div>
            {product.formulaProfile?.map((section, index) => (
              <div key={index}>
                <div className="typography-h1 text-primary mb-6">
                  {section.sectionTitle}
                </div>
                {section.sectionDescription && (
                  <p className="typography-body text-primary mb-6">
                    {section.sectionDescription}
                  </p>
                )}
                {section.sectionListItems.length > 0 && (
                  <ul className="list-disc list-inside space-y-4 typography-body text-primary mb-12">
                    {section.sectionListItems.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>

          <div className="mb-12">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-4 gap-y-6">
              {product.formulaIncludes?.map((item, index) => (
                <div key={index} className="flex flex-col">
                  <img
                    src={item.imageUrl}
                    alt={item.title}
                    className="w-12 h-12 mb-2"
                  />
                  <p className="typography-caption1 text-secondary">
                    {item.title}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="text-tertiary typography-btn-small">
              {product.disclaimer}
            </p>
          </div>

          {product.productReferences &&
            product.productReferences.length > 0 && (
              <div className="mt-10 pt-6">
                <h4 className="text-tertiary mb-4 typography-btn-small">
                  References:
                </h4>
                <ul className="list-none p-0">
                  {product.productReferences.map((ref, idx) => (
                    <li
                      className="mb-1 text-tertiary typography-btn-small"
                      key={idx}
                    >
                      <sup className="pr-1">{idx + 1}</sup>
                      {ref}
                    </li>
                  ))}
                </ul>
              </div>
            )}
        </div>
      </div>
    </section>
  );
};
